// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  statusCode?: number;
  timestamp?: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// ===== USD Wallets (gross/net) =====
export interface UsdWalletSummaryResponse {
  ownerType: string;
  ownerId: string;
  currency: string;
  walletId: string | null;
  balanceGross: string | number;
  balanceNet: string | number;
  status?: string;
  updatedAt?: string;
}

export interface UsdWalletLedgerResponse {
  ownerType: string;
  ownerId: string;
  currency: string;
  walletId: string | null;
  balanceGross?: string | number;
  balanceNet?: string | number;
  count: number;
  rows: any[];
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  sortWarning?: string;
}

export interface UsdWalletLedgerEntryResponse {
  row: Record<string, unknown>;
  ownerType: string | null;
  ownerId: string | null;
  currency: string;
}

/** Internal credit wallet (`wallets` + `wallet_transactions`). */
export interface CreditWalletSummaryResponse {
  ownerType: string;
  ownerId: string;
  walletId: string | null;
  balance: string | number;
  reservedBalance: string | number;
  currency: string;
  status?: string;
  updatedAt?: string;
}

export interface CreditWalletLedgerResponse {
  ownerType?: string | null;
  ownerId?: string | null;
  walletId?: string | null;
  balance?: string | number | null;
  reservedBalance?: string | number | null;
  currency?: string;
  count: number;
  rows: any[];
  items?: any[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary?: {
    totalTransactions: number;
    totalDeposits: number;
    totalWithdrawals: number;
    netFlow: number;
  };
}

export interface CreditWalletLedgerEntryResponse {
  item: Record<string, unknown>;
}

// User Types
export interface User {
  id: string;
  email?: string;
  /** Login/display handle from DB (`users.username`) */
  username?: string | null;
  userName?: string;
  name?: string;
  phone?: string | null;
  email_verified: boolean;
  phone_verified?: boolean;
  phone_verified_at?: string | null;
  email_verified_at?: string | null;
  status: 'active' | 'inactive' | 'suspended' | 'banned' | 'terminated';
  terminated_at?: string | null;
  terminated_by?: string | null;
  terminate_reason?: string | null;
  closeReleaseLog?: {
    closedByAdminId?: string | null;
    closedByName?: string | null;
    reason?: string | null;
    originalContacts?: {
      email?: string | null;
      phone?: string | null;
      username?: string | null;
      telegram_user_id?: string | null;
      telegram_username?: string | null;
    } | null;
    releasedContacts?: {
      email?: string | null;
      phone?: string | null;
      username?: string | null;
      telegram_user_id?: string | null;
      telegram_username?: string | null;
    } | null;
    createdAt?: string | null;
  } | null;
  banned: boolean;
  banned_at?: string;
  banned_by?: string;
  ban_reason?: string;
  shop_banned?: boolean;
  shop_ban_reason?: string | null;
  shop_ban_period?: '1d' | '3d' | '7d' | '30d' | 'permanent' | null;
  shop_ban_until?: string | null;
  shop_banned_at?: string | null;
  shop_ban_active?: boolean;
  admin_banned?: boolean;
  admin_ban_reason?: string | null;
  admin_ban_period?: '1d' | '3d' | '7d' | '30d' | 'permanent' | null;
  admin_ban_until?: string | null;
  admin_banned_at?: string | null;
  admin_ban_active?: boolean;
  last_login_at?: string;
  /** CDN kiosk player assignment */
  shop_id?: string | null;
  /** KIOSK-317 — joined signup bonus (null when the player never got one) */
  signupBonus?: SignupBonusGrant | null;
  shop?: {
    id: string;
    name: string;
    email?: string | null;
    agent?: {
      id: string;
      name: string;
      superAgent?: {
        id: string;
        name: string;
        email?: string | null;
        status?: string;
        phone?: string | null;
      } | null;
    } | null;
  } | null;
  profile: {
    firstName?: string;
    lastName?: string;
    role?: string;
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
  stats?: {
    totalDeposits: number;
    totalWithdrawals: number;
    walletBalance: string | number;
    totalTransactions: number;
  };
  recentTransactions?: Array<{
    id: string;
    transactionId: string;
    type: string;
    amount: number;
    status: string;
    paymentMethod: string;
    created_at: string;
  }>;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'banned' | 'terminated';
  banned?: boolean;
  bonus_status?: SignupBonusStatus | 'none';
  sortBy?: 'created_at' | 'last_login_at' | 'email' | 'phone' | 'status' | 'banned';
  sortOrder?: 'ASC' | 'DESC';
  /** Filter players whose shop belongs to this agent */
  agent_id?: string;
  /** Filter players assigned to this shop */
  shop_id?: string;
}

/** GET /admin/users/meta/agent-shop-options */
export interface AdminPlayerFilterAgent {
  id: string;
  name: string;
}

export interface AdminPlayerFilterShop {
  id: string;
  name: string;
  agentId: string;
  agentName: string | null;
}

export interface AdminPlayerFilterMetaResponse {
  success: boolean;
  message?: string;
  data?: {
    agents: AdminPlayerFilterAgent[];
    shops: AdminPlayerFilterShop[];
  };
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
  /** Sort the backend actually applied (may differ from the request after fallback). */
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface UsersResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    users: User[];
    pagination: Pagination;
  };
}

export interface UserDetailResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    user: User;
  };
}

export interface BanUserRequest {
  reason: string;
  ban_period: '1d' | '3d' | '7d' | '30d' | 'permanent';
}

export interface BanUserResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    userId: string;
    email: string;
    banned: boolean;
    banned_at: string;
    ban_reason?: string;
  };
}

export interface UnbanUserResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    userId: string;
    email: string;
    banned: boolean;
    unbanned_at: string;
  };
}

export interface TerminatePlayerPreviewProvider {
  gameProviderId: string;
  providerCode: string;
  providerName: string;
  recallMode: string;
  estimatedInGame: number;
  currency: string;
}

export interface TerminatePlayerPreviewBlocker {
  providerCode: string;
  recallMode: string;
  message: string;
}

export interface TerminatePlayerPreview {
  canTerminate: boolean;
  blockers: TerminatePlayerPreviewBlocker[];
  providers: TerminatePlayerPreviewProvider[];
  totals: {
    estimatedInGame: number;
    masterWalletBalance: number;
  };
  masterWalletCurrency: string;
}

export interface TerminatePlayerPreviewResponse {
  success: boolean;
  data: TerminatePlayerPreview;
}

export interface AgentTerminatePreviewBlocker {
  id: string;
  message: string;
  count?: number;
}

export interface AgentTerminatePreview {
  canTerminate: boolean;
  blockers: AgentTerminatePreviewBlocker[];
  counts?: {
    openShops: number;
    debtShops: number;
    activePlayers: number;
  };
}

export interface AgentTerminatePreviewResponse {
  success: boolean;
  data: AgentTerminatePreview;
}

export interface TerminatePlayerRequest {
  reason: string;
  confirm: true;
}

export interface CloseAndReleasePlayerRequest {
  reason: string;
  confirm: true;
}

export interface CloseAndReleasePlayerResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    originalContacts: {
      email: string | null;
      phone: string | null;
      username: string | null;
      telegram_user_id: string | null;
      telegram_username: string | null;
    };
    releasedContacts: {
      email: string | null;
      phone: string | null;
      username: string | null;
      telegram_user_id: string | null;
      telegram_username: string | null;
    };
  };
}

export interface TerminatePlayerResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    gameRecall: {
      providers: Array<{
        providerCode?: string;
        gameProviderId?: string;
        status: string;
        amount: number;
        error?: string | null;
      }>;
      totalRecalled: number;
      skippedCount?: number;
    };
  };
}

export interface UpdateUserStatusRequest {
  status: 'active' | 'banned' | 'suspended';
}

export interface UpdateUserStatusResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    userId: string;
    email: string;
    status: string;
  };
}

// Transaction Types
export interface Withdrawal {
  id: string;
  userId: string;
  amount: number;
  withdrawalType: 'bank_transfer' | 'bitcoin_transfer';
  status: 'pending' | 'awaiting_admin' | 'approved' | 'completed' | 'rejected' | 'failed';
  bankCode?: string;
  accountNumber?: string;
  accountName?: string;
  routingNumber?: string;
  description: string;
  createdAt: string;
  user: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

// Legacy WithdrawalFilters — see main WithdrawalFilters below for the full version


// Dashboard Types
export interface DashboardStats {
  users: {
    total: number;
    active: number;
    banned: number;
    newToday: number;
  };
  wallets: {
    totalBalance: number;
    totalTransactions: number;
    pendingWithdrawals: number;
  };
  transactions: {
    totalVolume: number;
    completedToday: number;
    pendingToday: number;
  };
  tickets: {
    total: number;
    open: number;
    inProgress: number;
    resolvedToday: number;
  };
}

// --- Dashboard (tabbed) types ---

export type DashboardPeriod = 'today' | 'week' | 'month' | '7d' | '30d' | 'custom';

export interface DashboardPeriodParams {
  period: DashboardPeriod;
  /** Inclusive calendar start (YYYY-MM-DD) — required when period=custom. */
  dateFrom?: string;
  /** Inclusive calendar end (YYYY-MM-DD) — required when period=custom. */
  dateTo?: string;
  /** Optional viewing timezone (IANA). Absent = platform timezone. */
  tz?: string;
}

/** Flow metric: value for the window, previous window value, % delta (null = N/A). */
export interface DashboardKpi {
  value: number;
  previous?: number;
  deltaPct?: number | null;
}

/**
 * Rollup freshness. `stale` means the hourly rollup worker has fallen behind;
 * the numbers are still correct (the backend widens its live query to cover the
 * gap) but they cost more to produce, and someone should look at the worker.
 */
export interface DashboardRollupFreshness {
  watermark_at: string | null;
  lag_seconds: number | null;
  stale: boolean;
}

/** v2 response envelope (all tab endpoints). */
export interface DashboardV2Envelope {
  period: DashboardPeriod;
  range_start: string;
  range_end: string;
  timezone: string;
  data_as_of: string;
  comparison: {
    range_start: string;
    range_end: string;
  };
  /** Absent on the portal endpoints, which have not adopted it yet. */
  rollup?: DashboardRollupFreshness;
}

export interface DashboardOverviewData extends DashboardV2Envelope {
  kpis: {
    platformNetPnl: DashboardKpi;
    usdIn: DashboardKpi;
    payoutsOut: DashboardKpi;
    netCashFlow: DashboardKpi;
    feesEarned: DashboardKpi;
    hierarchyCashCollected: DashboardKpi;
    wholesaleOnlineUsd: DashboardKpi;
    creditsIssued: DashboardKpi;
    playerCreditLiability: DashboardKpi;
    pendingWithdrawalsCount: DashboardKpi;
    pendingWithdrawalsAmount: DashboardKpi;
    onlineDebtOutstanding: DashboardKpi;
    shopFundingDebtOutstanding: DashboardKpi;
    tierUsdFloatTotal: DashboardKpi;
  };
  charts: {
    /** Legacy series, keyed on `date`. Unchanged. */
    dailyFlow: Array<{ date: string; usdIn: number; payoutsOut: number; net: number }>;
    granularity: DashboardGranularity;
    flow: DashboardFlowBucket[];
    fees: DashboardFeeBucket[];
    moneyIn: DashboardMoneyInBucket[];
    credits: DashboardCreditsBucket[];
  };
}

/** Chart bucket width: hourly for a single day, daily for week/month. */
export type DashboardGranularity = 'hour' | 'day';

export interface DashboardFlowBucket {
  bucket: string;
  usdIn: number;
  payoutsOut: number;
  net: number;
}

export interface DashboardFeeBucket {
  bucket: string;
  playerDeposits: number;
  withdrawals: number;
  creditPurchases: number;
}

export interface DashboardMoneyInBucket {
  bucket: string;
  platformPlayers: number;
  shopPlayers: number;
  network: number;
}

/** Player population per bucket. `totalPlayers` is the running population, not a delta. */
export interface DashboardPlayerBucket {
  bucket: string;
  newPlayers: number;
  activePlayers: number;
  totalPlayers: number;
}

export interface DashboardCreditsBucket {
  bucket: string;
  credits: number;
}

/** One segment's online deposits vs withdrawals per bucket. */
export interface DashboardSegmentFlowBucket {
  bucket: string;
  deposits: number;
  withdrawals: number;
}

export interface DashboardShopChannelBucket {
  bucket: string;
  onlineDeposits: number;
  counterDeposits: number;
  onlineWithdrawals: number;
  counterRedemptions: number;
}

export interface DashboardTierPurchaseBucket {
  bucket: string;
  superAgent: number;
  agent: number;
  shop: number;
}

export interface DashboardCashSettlementBucket {
  bucket: string;
  expected: number;
  received: number;
}

/**
 * Overview tab payload. KPIs are computed by the same backend function that
 * feeds the Financials tab, so the two tabs cannot disagree on a number.
 */
export interface DashboardExecutiveData extends DashboardV2Envelope {
  kpis: {
    platformNetPnl: DashboardKpi;
    usdIn: DashboardKpi;
    payoutsOut: DashboardKpi;
    netCashFlow: DashboardKpi;
    feesEarned: DashboardKpi;
    exposure: DashboardKpi;
    totalPlayers: DashboardKpi;
    newPlayers: DashboardKpi;
    activePlayers: DashboardKpi;
  };
  breakdowns: {
    revenueMix: Array<{ key: string; value: number }>;
    depositsByGateway: Array<{ provider: string; amount: number; count: number }>;
    creditSalesByTier: Array<{ tier: string; amount: number }>;
    liability: Array<{ key: string; value: number }>;
    cashSettlement: { expected: number; received: number; uncollected: number };
  };
  charts: {
    granularity: DashboardGranularity;
    flow: DashboardFlowBucket[];
    fees: DashboardFeeBucket[];
    moneyIn: DashboardMoneyInBucket[];
    players: DashboardPlayerBucket[];
  };
}

export interface DashboardPlatformPlayersData extends DashboardV2Envelope {
  kpis: {
    depositsAmount: DashboardKpi;
    depositsCount: DashboardKpi;
    avgDeposit: DashboardKpi;
    depositFailedRatePct: DashboardKpi;
    withdrawalsAmount: DashboardKpi;
    withdrawalsCount: DashboardKpi;
    pendingWithdrawals: DashboardKpi;
    pendingWithdrawalsAmount: DashboardKpi;
    playerCreditLiability: DashboardKpi;
    totalPlayers: DashboardKpi;
    newPlayers: DashboardKpi;
    activePlayers: DashboardKpi;
  };
  extras: {
    depositFunnel: {
      attempts: number;
      completed: number;
      failed: number;
    };
  };
  charts: {
    depositsByProvider: Array<{ provider: string; count: number; amount: number }>;
    granularity: DashboardGranularity;
    flow: DashboardSegmentFlowBucket[];
    players: DashboardPlayerBucket[];
  };
}

export interface DashboardShopPlayersData extends DashboardV2Envelope {
  kpis: {
    playerMoneyIn: DashboardKpi;
    shopNetProfit: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    onlineDepositsCount: DashboardKpi;
    counterDepositsCredits: DashboardKpi;
    counterDepositsCashReceived: DashboardKpi;
    onlineWithdrawalsGrossUsd: DashboardKpi;
    onlineWithdrawalsCount: DashboardKpi;
    counterRedemptionsCredits: DashboardKpi;
    counterRedemptionsCashPaid: DashboardKpi;
    tierFundingUsd: DashboardKpi;
    shopSpent: DashboardKpi;
    totalPlayers: DashboardKpi;
    newPlayers: DashboardKpi;
    activePlayers: DashboardKpi;
  };
  charts: {
    granularity: DashboardGranularity;
    channels: DashboardShopChannelBucket[];
    flow: DashboardSegmentFlowBucket[];
    players: DashboardPlayerBucket[];
  };
}

export interface DashboardAgentNetworkData extends DashboardV2Envelope {
  kpis: {
    creditPurchasesTotal: DashboardKpi;
    creditPurchasesSuperAgent: DashboardKpi;
    creditPurchasesAgent: DashboardKpi;
    creditPurchasesShop: DashboardKpi;
    creditsIssued: DashboardKpi;
    platformFeeRevenue: DashboardKpi;
    cashExpected: DashboardKpi;
    cashReceived: DashboardKpi;
    cashUncollected: DashboardKpi;
    onlineDebtOutstanding: DashboardKpi;
    tierUsdFloatTotal: DashboardKpi;
  };
  charts: {
    granularity: DashboardGranularity;
    purchasesByTier: DashboardTierPurchaseBucket[];
    cashSettlement: DashboardCashSettlementBucket[];
    credits: DashboardCreditsBucket[];
    /** Platform-wide concentration — same ranking idea as SA/Agent portals. */
    topAgents: Array<{ id: string; name: string; creditsPurchased: number }>;
    topShops: Array<{ id: string; name: string; creditsPurchased: number }>;
  };
}

/** @deprecated v1 window metadata — v2 uses DashboardV2Envelope */
export interface DashboardWindow {
  period: DashboardPeriod;
  timeZone: string;
  from: string;
  to: string;
  prevFrom: string;
  prevTo: string;
}

/** @deprecated use DashboardPlatformPlayersData */
export type DashboardPlayersData = DashboardPlatformPlayersData;

// Auth Types (Admin Login V2)
export interface LoginCredentialsRequest {
  usernameOrEmail: string;
  password: string;
  captchaToken?: string;
}

/** Login step 1 response: either token+admin (no 2FA) or needsOtp+pendingToken (2FA) */
export interface LoginCredentialsResponse {
  success: boolean;
  message: string;
  data:
    | { token: string; expiresAt: string; admin: AdminAccount }
    | { needsOtp: true; pendingToken: string; message?: string };
}

export interface VerifyOtpRequest {
  pendingToken: string;
  otp: string;
}

export interface ForgotPasswordRequestResponse {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    channel: 'email' | 'phone';
    expiresInSeconds: number;
    resend_count?: number;
    next_resend_at?: string;
    expires_at?: string;
  };
}

/** @deprecated Legacy flow - use LoginCredentialsRequest + verify-otp */
export interface SendCodeRequest {
  email: string;
}

/** @deprecated Legacy flow */
export interface SendCodeResponse {
  success: boolean;
  message: string;
}

/** @deprecated Legacy flow - use VerifyOtpRequest with verify-otp endpoint */
export interface LoginRequest {
  email: string;
  otp: string;
}

export type AdminRole = 'system_admin' | 'super_admin' | 'platform_admin' | 'finance_admin' | 'operation_admin' | 'support_admin';

// Permission types
export interface Permission {
  id: string;
  code: string;
  name: string;
  category: string;
  description: string;
  /** Game provider codes this admin is scoped to for this permission (e.g. walletManagement). */
  managedGameProviders?: string[] | null;
  created_at?: string;
  updated_at?: string;
}

export interface PermissionsListResponse {
  success: boolean;
  message: string;
  data: {
    permissions: Permission[];
    grouped?: {
      [category: string]: Permission[];
    };
  };
}

export interface AdminPermissionsResponse {
  success: boolean;
  message: string;
  data: {
    permissions: Permission[];
  };
}

export interface GrantPermissionsRequest {
  permissionCodes: string[];
  managedGameProviders?: string[];
}

export interface GrantPermissionsResponse {
  success: boolean;
  message: string;
  data: {
    granted: string[];
    permissions: Permission[];
  };
}

export interface RevokePermissionsRequest {
  permissionCodes: string[];
}

export interface RevokePermissionsResponse {
  success: boolean;
  message: string;
  data: {
    revoked: number;
    permissions: Permission[];
  };
}

export interface SetPermissionsRequest {
  permissionCodes: string[];
  managedGameProviders?: string[];
}

export interface SetPermissionsResponse {
  success: boolean;
  message: string;
  data: {
    permissions: Permission[];
  };
}

export interface AdminAccount {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  is_active: boolean;
  status?: string;
  last_login_at?: string | null;
  permissions?: string[];
  permissionObjects?: Permission[];
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    expiresAt: string;
    admin: AdminAccount;
  };
}

export interface AdminListFilters {
  page?: number;
  limit?: number;
  role?: string;
  active?: boolean;
  search?: string;
}

export interface AdminListResponse {
  success: boolean;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    items: AdminAccount[];
  };
}

export interface AdminDetailResponse {
  success: boolean;
  message: string;
  data: {
    admin: AdminAccount;
    permissions?: Permission[];
  };
}

export interface CreateAdminRequest {
  email: string;
  username: string;
  name: string;
  password: string;
  role: AdminRole;
}

export interface UpdateAdminRequest {
  is_active?: boolean;
  role?: AdminRole;
}

export interface ResetAdminPasswordRequest {
  password: string;
}

export interface UpdateMySettingsRequest {
  two_factor_enabled: boolean;
}

export interface UpdateMySettingsResponse {
  success: boolean;
  message: string;
  data: { two_factor_enabled: boolean };
}

export interface RequestEmailChangeRequest {
  new_email: string;
}

export interface RequestEmailChangeResponse {
  success: boolean;
  message: string;
  data?: { cooldownSeconds?: number };
}

export interface VerifyEmailChangeRequest {
  otp: string;
}

export interface VerifyEmailChangeResponse {
  success: boolean;
  message: string;
  data: { email: string };
}

// Dashboard types
export interface DashboardStats {
  users: {
    total: number;
    active: number;
    banned: number;
    newToday: number;
  };
  wallets: {
    totalBalance: number;
    totalTransactions: number;
    pendingWithdrawals: number;
  };
  transactions: {
    totalVolume: number;
    completedToday: number;
    pendingToday: number;
  };
  tickets: {
    total: number;
    open: number;
    inProgress: number;
    resolvedToday: number;
  };
}

export interface AdminPermissions {
  userManagement: boolean;
  walletManagement: boolean;
  transactionManagement: boolean;
  withdrawalManagement: boolean;
  ticketManagement: boolean;
  adminManagement: boolean;
  financeManagement: boolean;
}

// Form Types


export interface UpdateRoleRequest {
  role: 'user' | 'admin' | 'finance';
}

export interface CreditWalletRequest {
  amount: number;
  reason: string;
  description: string;
}

export interface DebitWalletRequest {
  amount: number;
  reason: string;
  description: string;
}


export interface CompleteWithdrawalRequest {
  adminNotes?: string;
}

export interface AddTicketResponseRequest {
  message: string;
  isInternal: boolean;
}

export interface TransactionOwnerParty {
  type: string;
  id: string;
  name?: string | null;
  roleLabel?: string;
  refLink?: string | null;
  displayLabel?: string;
}

export interface Transaction {
  id: number;
  transactionId: string;
  /** Gateway / provider reference id when present */
  externalTransactionId?: string | null;
  userId: string | null;
  /** `user` | `agent` | `shop` — CDN rows may omit userId */
  buyerType?: string | null;
  buyerId?: string | null;
  type: 'deposit' | 'withdrawal';
  /** Gateway provider: `linkmepay` | `btcpay` | `zeroxprocessing` | `meld` | `ggusonepay` */
  provider?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'failed' | 'cancelled' | 'expired';
  paymentMethod: string;
  description: string;
  linkMePayRequest?: Record<string, any>;
  /** Gateway callback payload when admin API includes it (e.g. 0xProcessing static wallet). */
  callbackData?: Record<string, unknown> | null;
  paymentResult?: Record<string, any>;
  errorMessage?: string | null;
  adminNotes?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  metadata?: {
    email?: string | null;
    allocations?: Array<{
      amount: number;
      gameProviderId: string;
      gameProviderCode: string;
      [key: string]: any;
    }>;
    isMultiGame?: boolean;
    allocationsCount?: number;
    [key: string]: any;
  };
  /** Parent payment_transactions.id (from sync_metadata.parentTransactionId) for allocation rows */
  parentInternalId?: string | null;
  /** Derived from child allocation rows (multi-game deposit). */
  fulfillmentStatus?: string | null;
  fulfillmentSummary?: {
    requestedAmount?: string;
    completedAmount?: string;
    pendingAmount?: string;
    failedAmount?: string;
    rejectedAmount?: string;
    refundedAmount?: string;
  } | null;
  /** True when row can expand to show per-game allocation children. */
  hasChildAllocations?: boolean;
  childAllocationCount?: number;
  syncMetadata?: Record<string, unknown>;
  gameProviderId?: string | null;
  created_at: string;
  updated_at: string;
  stats?: {
    processingTime?: string;
    relatedTransactions?: number;
    userTransactionCount?: number;
  };
  /** Party who paid / funded this transaction (payer tier). */
  depositor?: TransactionOwnerParty | null;
  /** Party who received credits / benefit (buyer or player). */
  recipient?: TransactionOwnerParty | null;
  fundingSummary?: {
    creditAmount?: number | null;
    cashCharged?: number | null;
    usdAmount?: number | null;
    costRateApplied?: number | null;
    fundingSource?: string | null;
    balanceToCredits?: boolean;
    buyerType?: string | null;
  } | null;
  relatedTransactions?: Array<{
    id: number;
    transactionId: string;
    buyerType?: string | null;
    buyerId?: string | null;
    type: 'deposit' | 'withdrawal';
    provider?: string;
    amount: number | string;
    currency: string;
    status: string;
    paymentMethod?: string | null;
    description?: string | null;
    metadata?: Record<string, any>;
    created_at: string;
    updated_at: string;
    refLink?: string;
    summaryLabel?: string;
    recipientDisplay?: string | null;
  }>;
  relatedWalletTransactions?: Array<{
    id: string;
    type: 'credit' | 'debit' | string;
    amount: number | string;
    balanceBefore?: number | string;
    balanceAfter?: number | string;
    status: string;
    transactionId?: string | null;
    referenceType?: string | null;
    referenceId?: string | null;
    description?: string | null;
    fromParty?: TransactionOwnerParty | null;
    toParty?: TransactionOwnerParty | null;
    flowLabel?: string;
    legSummary?: string;
    created_at: string;
    updated_at: string;
  }>;
  relatedUsdWalletTransactions?: Array<{
    id: string;
    type: 'deposit' | 'withdrawal' | 'adjustment' | 'reversal' | string;
    provider?: string | null;
    status: string;
    transactionId?: string | null;
    referenceType?: string | null;
    referenceId?: string | null;
    metadata?: Record<string, any>;
    amountGross?: number | string;
    amountNet?: number | string;
    balanceGrossBefore?: number | string;
    balanceGrossAfter?: number | string;
    balanceNetBefore?: number | string;
    balanceNetAfter?: number | string;
    ownerParty?: TransactionOwnerParty | null;
    ownerContact?: { email?: string | null; phone?: string | null } | null;
    flowLabel?: string;
    legSummary?: string;
    grossDisplay?: string;
    netDisplay?: string;
    feeBreakdown?: {
      items: Array<{
        key: string;
        label: string;
        amount: number;
        rate?: number | null;
      }>;
      totalFeeUsd: number;
      totalFeeRate?: number | null;
    } | null;
    created_at: string;
    updated_at: string;
  }>;
  user?: {
    id: string;
    /** Login/display handle from DB (`users.username`) */
    username?: string | null;
    email: string;
    phone?: string | null;
  };
  fraudFlagged?: boolean;
  fraudFlag?: {
    id?: string;
    flagType?: string;
    ruleOrReason?: string;
    severity?: string;
    flaggedAt?: string | null;
    isResolved?: boolean;
  } | null;
  fraudFlags?: Array<{
    id?: string;
    flagType?: string;
    ruleOrReason?: string;
    severity?: string;
    flaggedAt?: string | null;
    isResolved?: boolean;
  }>;
  flaggedInfo?: WithdrawalFlaggedInfo | null;
  /** Present on game_withdrawal detail (GET /admin/transactions/:id). */
  gameProvider?: {
    id: string;
    code: string | null;
    name: string | null;
    currency?: string | null;
  } | null;
  gameWithdrawalDetail?: {
    gameProvider?: {
      id: string;
      code: string | null;
      name: string | null;
      currency?: string | null;
    } | null;
    amount?: number | null;
    currency?: string;
    status?: string | null;
    direction?: string | null;
    transactionId?: string | null;
    walletBalanceBefore?: number | null;
    walletBalanceAfter?: number | null;
    gameBalanceBefore?: number | null;
    gameBalanceAfter?: number | null;
    createdAt?: string | null;
  } | null;
  gameWalletTransaction?: {
    id: string;
    status: string;
    direction: string;
    amount: number | null;
    walletBalanceBefore?: number | null;
    walletBalanceAfter?: number | null;
    gameBalanceBefore?: number | null;
    gameBalanceAfter?: number | null;
    createdAt?: string | null;
    updatedAt?: string | null;
  } | null;
}

export interface TransactionFilters {
  page?: number;
  limit?: number;
  /** List tab: players (user) | agents | shops | super agents */
  buyerScope?: 'user' | 'agent' | 'shop' | 'super_agent';
  search?: string;
  email?: string;
  phone?: string;
  type?: 'deposit' | 'withdrawal';
  status?: 'pending' | 'approved' | 'rejected' | 'completed' | 'failed' | 'cancelled' | 'expired';
  /** Filter by gateway: `linkmepay` | `btcpay` | `zeroxprocessing` | `meld` | `ggusonepay` */
  provider?: string;
  paymentMethod?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'created_at' | 'amount' | 'creditAmount' | 'status' | 'type';
  sortOrder?: 'ASC' | 'DESC';
}

export interface TransactionAllocation {
  id: number;
  transactionId: string;
  parentInternalId: string;
  type: string;
  provider?: string;
  amount: number | string;
  currency: string;
  status: string;
  displayStatus: string;
  paymentMethod?: string | null;
  description?: string | null;
  errorMessage?: string | null;
  gameProviderId?: string | null;
  gameProviderCode?: string | null;
  gameProviderName?: string | null;
  created_at?: string;
  updated_at?: string;
  syncStatus?: string | null;
  refundTransactionId?: string | null;
}

export interface TransactionAllocationsResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    parentId: number;
    allocations: TransactionAllocation[];
  };
}

export interface TransactionsResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    transactions: Transaction[];
    pagination: Pagination;
  };
}

export interface TransactionDetailResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    transaction: Transaction;
  };
}

export interface TransactionStatsResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    overview: {
      totalTransactions: number;
      totalAmount: number;
      completedTransactions: number;
      pendingTransactions: number;
      failedTransactions: number;
    };
    byType: {
      totalDeposits: number;
      totalWithdrawals: number;
    };
    byMethod: {
      deposits: Array<{
        provider?: string;
        paymentMethod: string;
        count: number;
        total: number;
      }>;
      withdrawals: Array<{
        provider?: string;
        paymentMethod: string;
        count: number;
        total: number;
      }>;
    };
  };
}

export interface UpdateTransactionStatusRequest {
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'failed' | 'cancelled';
  adminNotes?: string;
}

export interface UpdateTransactionStatusResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    transactionId: string;
    status: string;
    adminNotes?: string;
  };
}

// Wallet Types (Admin Panel)
export interface Wallet {
  id: string;
  userId: string;
  balance: string;
  totalBalance: string;
  reservedBalance: string;
  availableBalance: string;
  currency: string;
  lockedBalance: string;
  createdAt: string;
  updatedAt: string;
}

export interface WalletStats {
  totalTransactions: number;
  totalCredits: number;
  totalDebits: number;
  netAmount: number;
  lastTransactionDate?: string | null;
}

/** Realized player finance totals (admin player detail). */
export interface PlayerFinanceStats {
  totalDepositToWallet: number;
  totalCredit: number;
  totalCashOutFromWallet: number;
  totalMasterToGame: number;
  totalGameToMaster: number;
  /** Current master wallet balance (balance = walletPnl + gamesPnl + totalCredit). */
  currentWalletBalance: number;
  /** walletPnl = currentWalletBalance − gamesPnl − totalCredit */
  walletPnl: number;
  /** gamesPnl = gameToMaster − masterToGame */
  gamesPnl: number;
}

export interface WalletTransaction {
  id: string;
  userId: string;
  walletId?: string;
  type: 'credit' | 'debit' | 'deposit' | 'withdrawal' | 'transfer' | 'bonus' | 'adjustment';
  amount: number | string;
  balance: number | string;
  balanceBefore?: number;
  balanceAfter?: number;
  description: string;
  referenceId?: string;
  referenceType?: string;
  status?: string;
  metadata?: Record<string, any>;
  /** API may expose camelCase from Sequelize before normalization */
  createdAt?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface WalletTransactionFilters {
  page?: number;
  limit?: number;
  type?: 'deposit' | 'withdrawal' | 'credit' | 'debit' | 'transfer' | 'bonus' | 'adjustment' | '';
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'created_at' | 'amount' | 'type';
  sortOrder?: 'ASC' | 'DESC';
}

export interface WalletResponse {
  success: boolean;
  message: string;
  statusCode?: number;
  timestamp?: string;
  data: {
    wallet: Wallet;
    user: {
      id: string;
      email: string;
      phone?: string | null;
      status: string;
      banned: boolean;
    };
    stats: WalletStats;
    financeStats?: PlayerFinanceStats;
  };
}

export interface WalletTransactionsResponse {
  success: boolean;
  message: string;
  statusCode?: number;
  timestamp?: string;
  data: {
    transactions: WalletTransaction[];
    pagination: Pagination;
    user: {
      id: string;
      email: string;
    };
  };
}

// Reserved Balance Types
export interface ReservedBalanceItem {
  transactionId: string;
  amount: number;
  currency: string;
  status: string;
  type: 'withdrawal' | 'deposit';
  withdrawalType?: string | null;
  paymentMethod?: string;
  createdAt: string;
  hasReservation: boolean;
  linkedPaymentTransactionId?: string | null;
}

export interface ReservedBalanceDetailResponse {
  success: boolean;
  message: string;
  data: {
    reservedBalance: number;
    totalBalance: number;
    availableBalance: number;
    currency: string;
    items: ReservedBalanceItem[];
    user: {
      id: string;
      email: string;
      status: string;
    };
  };
}

// ===== WITHDRAWAL REQUEST TYPES =====

export interface WithdrawalFlaggedInfo {
  isFlagged: boolean;
  flagTypes?: string[];
  flags?: Array<{
    flagType: string;
    severity?: string;
    isResolved?: boolean;
    createdAt?: string;
  }>;
  reasons?: Array<{
    flagType: string;
    description: string;
  }>;
  analysis?: {
    withdrawalThresholdExceeded?: boolean;
    withdrawalThreshold?: number;
    withdrawalThresholdType?: string | null;
    profitRatio?: number;
    netProfit?: number;
    [key: string]: unknown;
  } | null;
  opsReviewOnly?: boolean;
}

export interface WithdrawalRequest {
  transactionId: string;
  userId: string | null;
  /** `user` | `agent` | `shop` | `super_agent` — CDN / portal rows may omit userId */
  buyerType?: string | null;
  buyerId?: string | null;
  metadata?: Record<string, unknown> | null;
  /** Resolved from buyer identity maps (agent/shop/super agent/user). */
  requesterDisplayName?: string | null;
  requesterDisplayLabel?: string | null;
  requesterEmail?: string | null;
  requesterPhone?: string | null;
  user: {
    id: string;
    email: string;
    phone?: string | null;
    /** Player assigned shop (null => system player) */
    shop_id?: string | null;
    shopId?: string | null;
    profile: {
      firstName: string;
      lastName: string;
    };
  } | null;
  withdrawalType?: 'bank_transfer' | 'bitcoin_transfer' | 'litecoin_transfer' | 'dogecoin_transfer' | 'pyusd_transfer' | 'ecash_transfer' | 'game_withdrawal' | string | null;
  amount: number;
  currency: string;
  description: string;
  status:
    | 'pending'
    | 'processing'
    | 'awaiting_admin'
    | 'approved'
    | 'retry_scheduled'
    | 'completed'
    | 'rejected'
    | 'failed'
    | 'cancelled'
    | 'expired';
  adminNotes?: string;
  /** DB `error_message` — gateway / system failure text */
  errorMessage?: string | null;
  /** DB `sync_metadata` — e.g. `payoutRefund.refundReason` for BTCPay cancel */
  syncMetadata?: Record<string, unknown> | null;
  approvedAt?: string;
  approvedBy?: string;
  approvedByEmail?: string;
  createdAt: string;
  updatedAt: string;
  bankInfo?: {
    bankCode: string;
    accountNumber: string;
    accountName: string;
    routingNumber: string;
    accountType?: string;
  };
  bitcoinInfo?: {
    bitcoinAddress: string;
    bitcoinNetwork: string;
    memo?: string | null;
  };
  pyusdInfo?: {
    pyusdAddress?: string;
    network?: string;
  };
  ecashInfo?: {
    ecashAccount?: string;
    accountName?: string;
  };
  /** Gateway provider: `linkmepay` | `btcpay` | `zeroxprocessing` | `ggusonepay` (for crypto/bank payout) */
  provider?: string | null;
  /** Game withdrawal Phase 3 local settlement failed after retries */
  phase3RecoveryPending?: boolean;
  recoveryReason?: string | null;
  phase3Error?: string | null;
  feeSnapshot?: {
    feeModel?: string | null;
    netPayoutUsd?: number | null;
    systemFeeRate?: number | null;
    systemFeeAmount?: number | null;
    gatewayFeeRate?: number | null;
    gatewayFeeAmount?: number | null;
    networkFeeType?: string | null;
    networkFeeRate?: number | null;
    networkFeeAmount?: number | null;
    totalFeeUsd?: number | null;
    gatewayPayoutUsd?: number | null;
    tierFundingUsd?: number | null;
    source?: string | null;
  } | null;
  configuredPortalFee?: {
    source?: string;
    provider?: string;
    operation?: string;
    methodId?: string;
    matchedMethodId?: string;
    paymentGateFeeRate?: number;
    paymentGateFeeMinUsd?: number;
    systemFeeRate?: number;
    feeRate?: number;
    networkFeeType?: string;
    networkFeeRate?: number;
    networkFeeAmount?: number;
    isEnabled?: boolean;
  } | null;
  configuredProviderFee?: {
    source?: string;
    provider?: string;
    operation?: string;
    methodId?: string;
    matchedMethodId?: string;
    paymentGateFeeRate?: number;
    paymentGateFeeMinUsd?: number;
    systemFeeRate?: number;
    feeRate?: number;
    networkFeeType?: string;
    networkFeeRate?: number;
    networkFeeAmount?: number;
    isEnabled?: boolean;
  } | null;
  expectedPayoutBreakdown?: {
    sentToProviderUsd?: number | null;
    expectedNetworkFeeUsd?: number | null;
    expectedNetworkFeeSource?: string | null;
    expectedReceivedUsd?: number | null;
    isEstimate?: boolean;
  } | null;
  /**
   * When true, admin UI should not approve this withdrawal. It must be approved by the owning shop.
   * Computed by backend using configured threshold and withdrawal context.
   */
  requiresShopApproval?: boolean;
  /** Present when status is `awaiting_admin` — fraud / manual review context from backend */
  flaggedInfo?: WithdrawalFlaggedInfo | null;
}

export interface WithdrawalFilters {
  page?: number;
  limit?: number;
  status?:
    | 'pending'
    | 'processing'
    | 'awaiting_admin'
    | 'approved'
    | 'retry_scheduled'
    | 'completed'
    | 'rejected'
    | 'failed'
    | 'cancelled'
    | 'expired';
  withdrawalType?: 'bank_transfer' | 'bitcoin_transfer' | 'litecoin_transfer' | 'dogecoin_transfer' | 'pyusd_transfer' | 'ecash_transfer' | 'game_withdrawal';
  /** Partial match on transaction ID */
  search?: string;
  /** Exact match on user phone (E.164, leading + optional) */
  phone?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'createdAt' | 'amount' | 'status';
  sortOrder?: 'ASC' | 'DESC';
}

export interface WithdrawalsResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    withdrawals: WithdrawalRequest[];
    pagination: Pagination;
  };
}

export interface ApproveWithdrawalRequest {
  adminNotes?: string;
}

export interface ApproveWithdrawalResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    transactionId: string;
    status: string;
    amount: number;
    currency: string;
    withdrawalType: string;
    message: string;
  };
}

export interface RejectWithdrawalRequest {
  reason: string;
}

export interface RejectWithdrawalResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    transactionId: string;
    status: string;
    adminNotes?: string;
  };
}

export interface CompleteWithdrawalRequest {
  adminNotes?: string;
}

export interface CompleteWithdrawalResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    transactionId: string;
    status: string;
    adminNotes?: string;
  };
}

export interface PayoutOrder {
  payoutOrderId: string;
  status: 'pending' | 'payoutCompleted' | 'payoutFail';
  createdAt: string;
  errorMessage: string | null;
  retryCount: number;
  lastRetryAt: string | null;
  canRetry: boolean;
  retryHistory: any[];
  currentAttempt: {
    attempt: number;
    timestamp: string;
    status: string;
    payoutOrderId: string;
  };
  requestData: Record<string, any>;
  responseData: Record<string, any>;
}

export interface WithdrawalPayoutLogResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    transactionId: string;
    payoutOrder: PayoutOrder | null;
  };
}

// Support Ticket Types
export interface SupportTicket {
  id: string;
  ticketNumber: string;
  email: string;
  phoneNumber: string;
  category: string;
  categoryDisplayName: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed' | 'done';
  message: string;
  customerServiceNotes?: string;
  userTier?: string;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  estimatedResponseTime?: string;
  replies?: TicketReply[];
}

/** User tiers for support ticket prioritization */
export const SUPPORT_TIER_OPTIONS = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'] as const;
export type SupportTier = (typeof SUPPORT_TIER_OPTIONS)[number];
export const PRIORITY_TIERS: SupportTier[] = ['Platinum', 'Diamond'];

export interface TicketReply {
  id: string;
  replyType: 'admin' | 'user';
  userId?: string;
  email?: string;
  message: string;
  adminName?: string;
  createdAt: string;
}

export interface SupportCategory {
  id: string;
  display_name: string;
  description: string;
  estimated_response_time: string;
  is_active: boolean;
}

export interface TicketFilters {
  page?: number;
  limit?: number;
  // Only allow filtering by active statuses in the new flow
  status?: 'pending' | 'in_progress' | 'resolved' | 'closed';
  /** Filter by user tier (partial match). Use "Platinum" or "Diamond" for priority. */
  tier?: string;
}

export interface TicketsResponse {
  success: boolean;
  data: {
    tickets: SupportTicket[];
    pagination: {
      total: number;
      limit: number;
      // Page-based pagination (for regular list)
      page?: number;
      totalPages?: number;
      // Cursor-based pagination (for search)
      hasNextPage?: boolean;
      hasPrevPage?: boolean;
      nextCursor?: string | null;
      prevCursor?: string | null;
    };
  };
}

export interface TicketResponse {
  success: boolean;
  data: SupportTicket;
}

export interface SubmitTicketRequest {
  email: string;
  phoneNumber: string;
  category_id: string;
  message: string;
}

export interface SubmitTicketResponse {
  success: boolean;
  message: string;
  data: {
    success: boolean;
    ticket: SupportTicket;
    remainingCapacity: number;
  };
}

export interface ReplyTicketRequest {
  message: string;
}

export interface ReplyTicketResponse {
  success: boolean;
  message: string;
  data: {
    success: boolean;
    reply: TicketReply;
    ticket: {
      ticketNumber: string;
      status: string;
    };
  };
}

export interface UpdateTicketStatusRequest {
  // Only allow updating to active statuses in the new flow
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  notes?: string;
}

export interface UpdateTicketStatusResponse {
  success: boolean;
  message: string;
  data: {
    success: boolean;
    ticket: {
      id: string;
      ticketNumber: string;
      status: string;
      customerServiceNotes?: string;
      resolvedAt?: string;
      resolvedBy?: string;
    };
  };
}

export interface MarkTicketDoneResponse {
  success: boolean;
  message: string;
  data: {
    success: boolean;
    ticket: {
      id: string;
      ticketNumber: string;
      status: string;
      resolvedAt: string;
      resolvedBy: string;
    };
  };
}

export interface SupportStats {
  total: number;
  recent: number;
  byStatus: Array<{
    status: string;
    count: string;
  }>;
  byCategory: Array<{
    category: string;
    count: string;
  }>;
}

export interface SupportStatsResponse {
  success: boolean;
  data: SupportStats;
}

export interface TicketCapacityResponse {
  success: boolean;
  data: {
    current: number;
    limit: number;
    remaining: number;
    canSubmit: boolean;
  };
}

// ===== GAME WALLET TYPES =====

export type GameWalletSyncStatus = 'never_synced' | 'in_sync' | 'pending' | 'failed';

export type GameWalletTransactionDirection = 'master_to_game' | 'game_to_master' | 'adjustment';

export type GameWalletTransactionStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface GameWallet {
  id: string;
  userId: string;
  gameProviderId: string;
  walletBalance: string;
  inGameBalance: string;
  syncStatus: GameWalletSyncStatus;
  lastSyncedAt: string | null;
  lastSyncSource: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  gameUsername?: string;
  errorLogs?: GameWalletErrorLog[];
  user?: {
    id: string;
    email: string | null;
    phone?: string | null;
    status: string;
    banned: boolean;
  };
  provider?: {
    id: string;
    code: string;
    name: string;
    status: string;
    currency: string;
    min_deposit?: string;
    max_deposit?: string;
  };
}

export interface GameWalletErrorLog {
  id: string;
  providerCode: string;
  apiMethod: string;
  requestUrl?: string | null;
  requestMethod?: string | null;
  responseStatus?: number | null;
  errorMessage?: string | null;
  errorStack?: string | null;
  durationMs?: number | null;
  paymentTransactionId?: number | null;
  paymentTransaction?: {
    id: number;
    transactionId: string;
    amount: string;
    syncStatus: string;
    created_at: string;
  };
  referenceId?: string | null;
  requestBody?: Record<string, any> | null;
  responseBody?: Record<string, any> | null;
  created_at: string;
}

export interface GameWalletDetail extends GameWallet {
  transactionCount?: number;
  errorLogs?: GameWalletErrorLog[];
  user: {
    id: string;
    email: string;
    phone?: string | null;
    status: string;
    banned: boolean;
    created_at?: string;
  };
  provider: {
    id: string;
    code: string;
    name: string;
    status: string;
    currency: string;
    min_deposit?: string;
    max_deposit?: string;
  };
}

export interface GameWalletTransaction {
  id: string;
  userId: string;
  gameProviderId: string;
  userGameWalletId: string;
  paymentTransactionId: number | null;
  direction: GameWalletTransactionDirection;
  status: GameWalletTransactionStatus;
  amount: string;
  walletBalanceBefore: string;
  walletBalanceAfter: string;
  gameBalanceBefore: string;
  gameBalanceAfter: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
  };
  provider?: {
    id: string;
    code: string;
    name: string;
  };
  paymentTransaction?: {
    id: number;
    transactionId?: string;
    status: string;
    syncMetadata?: {
      providerReviewPending?: boolean;
      providerReviewReason?: string | null;
      ambiguousError?: string | null;
      [key: string]: unknown;
    };
  };
}

export interface GameWalletFilters {
  page?: number;
  limit?: number;
  userId?: string;
  gameProviderId?: string;
  syncStatus?: GameWalletSyncStatus;
  /** Search by phone, email, or user game account */
  search?: string;
  sortBy?: 'walletBalance' | 'inGameBalance' | 'lastSyncedAt';
  sortOrder?: 'ASC' | 'DESC';
}

export interface GameWalletTransactionFilters {
  page?: number;
  limit?: number;
  direction?: GameWalletTransactionDirection;
  status?: GameWalletTransactionStatus;
  startDate?: string;
  endDate?: string;
  sortBy?: 'amount' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
}

export interface ProviderTransactionFilters extends GameWalletTransactionFilters {
  gameProviderId: string;
}

export interface ProviderHealthData {
  hours: number;
  totalCalls: number;
  availability: {
    status: 'healthy' | 'degraded' | 'critical' | 'unknown';
    lastCallAt: string | null;
    successRatePct: number;
  };
  avgResponseMs: number | null;
  errorRatePct: number;
  recentErrors: Array<{
    apiMethod: string;
    errorMessage: string | null;
    responseStatus: number | null;
    created_at: string;
  }>;
}

export interface ProviderHealthResponse {
  success: boolean;
  message: string;
  data: ProviderHealthData;
}

export interface ProviderSyncLogItem {
  id: string;
  apiMethod: string;
  success: boolean;
  durationMs: number | null;
  responseStatus: number | null;
  errorMessage: string | null;
  created_at: string;
}

export interface ProviderSyncLogsFilters {
  gameProviderId: string;
  page?: number;
  limit?: number;
  apiMethod?: string;
  success?: boolean;
}

export interface ProviderSyncLogsResponse {
  success: boolean;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    items: ProviderSyncLogItem[];
  };
}

export interface GameWalletListResponse {
  success: boolean;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    items: GameWallet[];
    sortBy?: string | null;
    sortOrder?: 'ASC' | 'DESC' | null;
    sortWarning?: string;
  };
}

export interface GameWalletDetailResponse {
  success: boolean;
  message: string;
  data: GameWalletDetail;
}

export interface GameWalletTransactionsResponse {
  success: boolean;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    items: GameWalletTransaction[];
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    sortWarning?: string;
  };
}

export interface ProviderReviewContext {
  transaction: {
    id: number;
    transactionId: string;
    type: string;
    amount: string;
    status: string;
    syncStatus: string;
    gameProviderId: string;
    userId: string;
    createdAt: string;
    updatedAt: string;
  };
  gameProvider: {
    id: string;
    code: string;
    name: string;
  } | null;
  baseline: {
    providerBalanceBefore?: number;
    localInGameBalanceBefore?: number;
    capturedAt?: string;
  } | null;
  current: {
    providerInGameBalance: number | null;
    localInGameBalance: number | null;
    walletBalance: number;
    masterWalletNote: string;
  };
  deltas: {
    providerDelta: number;
    localDelta: number | null;
    expectedAmount: number;
  } | null;
  reconcile: {
    attempts: number;
    lastOutcome: string | null;
    lastReconcile: Record<string, unknown> | null;
    suggestedOutcome: string;
    ambiguousSince: string | null;
    ambiguousError: string | null;
  };
  playerGuidance: {
    blockGameDepositWithdraw: boolean;
    blockAllPayouts: boolean;
    message: string;
  };
}

export interface ProviderReviewContextResponse {
  success: boolean;
  message: string;
  data: ProviderReviewContext;
}

export interface ReconcileProviderReviewRequest {
  adminNotes?: string;
}

export interface ReconcileProviderReviewResponse {
  success: boolean;
  message: string;
  data: {
    transactionId: string;
    status: string;
    syncStatus: string;
    outcome: string;
    reconcileSnapshot: Record<string, unknown> | null;
  };
}

export interface SyncWalletResponse {
  success: boolean;
  message: string;
  data: {
    wallet: GameWallet;
  };
}

export interface GameWalletStatistics {
  totalWallets: number;
  totalWalletBalance: string;
  totalInGameBalance: string;
  totalBalance: string;
  syncStatusCounts: {
    never_synced: number;
    in_sync: number;
    pending: number;
    failed: number;
  };
}

export interface GameWalletStatisticsResponse {
  success: boolean;
  message: string;
  data: GameWalletStatistics;
}

// ===== GAME PROVIDER TYPES =====

export interface GameProvider {
  id: string;
  code: string;
  name: string;
  currency: string;
  min_deposit?: string;
  max_deposit?: string;
  minDeposit?: number;
  maxDeposit?: number;
  status: string;
  /** Kiosk list order (ASC). Default 1000 = end */
  displayOrder?: number;
  display_order?: number;
  /** Badge on kiosk card; empty/null = hidden */
  clientTag?: string | null;
  client_tag?: string | null;
  config?: Record<string, any>;
  metadata?: Record<string, any>;
  adminLink?: string;
  gameUrl?: string;
  adminAccountLogin?: {
    username?: string;
    password?: string;
    note?: string;
  };
  adminInfo?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface GameProvidersResponse {
  success: boolean;
  message: string;
  data: {
    items: GameProvider[];
  };
}

export interface GameProviderDetailResponse {
  success: boolean;
  message: string;
  data: GameProvider;
}

export interface CreateGameProviderRequest {
  code: string;
  name: string;
  currency: string;
  minDeposit?: number;
  maxDeposit?: number;
  status?: string;
  config?: Record<string, any>;
  metadata?: Record<string, any>;
  adminLink?: string;
  gameUrl?: string;
  adminAccountLogin?: {
    username?: string;
    password?: string;
    note?: string;
  };
  adminInfo?: Record<string, any>;
  displayOrder?: number;
  clientTag?: string | null;
}

export interface CreateGameProviderResponse {
  success: boolean;
  message: string;
  data: GameProvider;
}

export interface UpdateGameProviderRequest {
  name?: string;
  currency?: string;
  minDeposit?: number;
  maxDeposit?: number;
  status?: string;
  config?: Record<string, any>;
  metadata?: Record<string, any>;
  adminLink?: string | null;
  gameUrl?: string | null;
  adminAccountLogin?: {
    username?: string;
    password?: string;
    note?: string;
  } | null;
  adminInfo?: Record<string, any> | null;
  displayOrder?: number | null;
  clientTag?: string | null;
}

export interface ReorderGameProvidersResponse {
  success: boolean;
  message: string;
  data?: { ok?: boolean };
}

export interface UpdateGameProviderResponse {
  success: boolean;
  message: string;
  data: GameProvider;
}

export interface DeleteGameProviderResponse {
  success: boolean;
  message: string;
}

// ===== AGENT BALANCES (BY GAME) TYPES =====

export type AgentBalanceHealthStatus = 'healthy' | 'out_of_sync' | 'down_risk' | 'na';

export interface AgentBalanceItem {
  gameProviderId: string;
  providerCode: string;
  providerName: string;
  currency: string;
  status: string | null;
  balance: number | null;
  lastSyncedAt: string | null;
  supportsSync?: boolean;
  lastSyncStatus?: 'success' | 'failed' | null;
  consecutiveFailCount?: number;
  lastAttemptAt?: string | null;
  lastSyncError?: string | null;
  healthStatus?: AgentBalanceHealthStatus;
}

export interface AgentBalancesFilters {
  gameProviderId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface AgentBalancesResponse {
  success: boolean;
  message: string;
  data: {
    items: AgentBalanceItem[];
    total: number;
    page: number;
    limit: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  };
}

export interface AgentBalanceHealthSnapshot {
  capturedAt: string;
  healthy: number;
  outOfSync: number;
  downRisk: number;
  na: number;
}

export interface AgentBalanceHealthSnapshotsResponse {
  success: boolean;
  message: string;
  data: {
    items: AgentBalanceHealthSnapshot[];
  };
}

export interface AgentBalancesSyncResponse {
  success: boolean;
  message: string;
  data: {
    synced: number;
    failed: number;
    errors?: Array<{ code: string; error: string }>;
  };
}

// ===== FULL TRANSACTION & RETRY SYNC TYPES =====

export interface FullTransactionDetail {
  transaction: {
    id: number;
    transactionId: string;
    userId: string;
    type: 'deposit' | 'withdrawal';
    amount: number;
    currency: string;
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    paymentMethod: string;
    description: string;
    syncStatus?: 'pending' | 'completed' | 'failed';
    syncMetadata?: Record<string, any>;
    linkMePayRequest?: Record<string, any>;
    paymentResult?: Record<string, any>;
    adminNotes?: string | null;
    approvedAt?: string | null;
    approvedBy?: string | null;
    created_at: string;
    updated_at: string;
  };
  user: {
    id: string;
    email: string;
    phone?: string | null;
    status: string;
    banned: boolean;
  };
  walletBalance?: string;
  gameProvider?: {
    id: string;
    code: string;
    name: string;
    status: string;
    currency: string;
  };
  gameWallet?: {
    id: string;
    walletBalance: string;
    inGameBalance: string;
    syncStatus: string;
    lastSyncedAt: string | null;
  };
  gameWalletTransactions?: Array<{
    id: string;
    direction: string;
    status: string;
    amount: string;
    walletBalanceBefore: string;
    walletBalanceAfter: string;
    gameBalanceBefore: string;
    gameBalanceAfter: string;
    created_at: string;
  }>;
  summary: {
    canRetrySync: boolean;
    hasGameWallet: boolean;
    pendingAmount?: string;
  };
}

export interface FullTransactionDetailResponse {
  success: boolean;
  message: string;
  data: FullTransactionDetail;
}

export interface RetrySyncRequest {
  reason?: string;
}

export interface RetrySyncResponse {
  success: boolean;
  message: string;
  data: {
    transactionId: string;
    syncStatus: string;
    retryCount?: number;
  };
}

export interface FailedTransaction {
  id: number;
  transactionId: string;
  userId: string;
  type: 'deposit' | 'withdrawal';
  amount: number;
  currency: string;
  status: string;
  paymentMethod: string;
  syncStatus: 'failed';
  syncMetadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
  };
  gameProvider?: {
    id: string;
    code: string;
    name: string;
  };
}

export interface FailedTransactionsFilters {
  page?: number;
  limit?: number;
  gameProviderId?: string;
  startDate?: string;
  endDate?: string;
}

export interface FailedTransactionsResponse {
  success: boolean;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    items: FailedTransaction[];
  };
}

// ===== REFUND TRANSACTION TYPES =====

export interface RefundTransactionRequest {
  reason: string;
}

export interface RefundTransactionResponse {
  success: boolean;
  message: string;
  data: {
    transactionId: number;
    status: string;
    refundedAt: string;
  };
}

export interface RefundAllTransactionsRequest {
  reason: string;
  filters: {
    userId: string;
    gameProviderId: string;
  };
}

export interface RefundAllTransactionsResponse {
  success: boolean;
  message: string;
  data: {
    refundedCount: number;
    refundedAt: string;
  };
}

// ===== VFX CONFIGURATION TYPES =====

export type VfxType =
  | 'snow'
  | 'confetti'
  | 'fireworks'
  | 'bubbles'
  | 'stars'
  | 'particles_connect'
  | 'absorbers'
  | 'polygons'
  | 'emitters'
  | 'sparkles';

export type VfxIntensity = 'light' | 'medium' | 'heavy';

export interface VfxConfig {
  enabled: boolean;
  type: VfxType | null;
  intensity: VfxIntensity | null;
}

export interface BackgroundConfig {
  enabled: boolean;
  url: string | null;
  fallback: string;
  theme: number; // 0 = no theme, 1-10 = theme number
}

export interface VfxConfigurationData {
  vfx: VfxConfig;
  background: BackgroundConfig;
  app_themes: number | null;
  version: string;
  updatedAt: string;
  updatedBy: string | {
    id: string;
    email: string;
    name: string;
  } | null;
}

export interface VfxConfigurationResponse {
  success: boolean;
  message?: string;
  data: VfxConfigurationData;
  etag?: string;
}

export interface UpdateVfxConfigRequest {
  vfx?: {
    enabled: boolean;
    type?: VfxType;
    intensity?: VfxIntensity;
  };
  background?: {
    enabled: boolean;
    url?: string;
    fallback?: string;
    theme?: number;
  };
  app_themes?: number | null;
}

export interface PresetBackground {
  id: string;
  name: string;
  description: string;
  url: string;
  thumbnail: string;
}

export interface PresetBackgroundsResponse {
  success: boolean;
  message: string;
  data: PresetBackground[];
}

// ===== VFX SCHEDULE TYPES =====

export interface VfxSchedule {
  id: string;
  name: string;
  startTime: string; // ISO 8601
  endTime: string; // ISO 8601
  isActive: boolean;
  vfx: VfxConfig;
  background: BackgroundConfig;
  app_themes: number | string | null; // Can be number or string from API
  createdAt?: string;
  updatedAt?: string;
  createdBy?: {
    id: string;
    email: string;
    name: string;
  };
}

export interface VfxScheduleListResponse {
  success: boolean;
  message: string;
  data: VfxSchedule[];
}

export interface VfxScheduleDetailResponse {
  success: boolean;
  message: string;
  data: VfxSchedule;
}

export interface CreateVfxScheduleRequest {
  name: string;
  start_time: string; // ISO 8601
  end_time: string; // ISO 8601
  is_active: boolean;
  vfx: {
    enabled: boolean;
    type?: VfxType;
    intensity?: VfxIntensity;
  };
  background: {
    enabled: boolean;
    url?: string | null;
    fallback?: string;
    theme?: number;
  };
  app_themes?: number | null;
}

export interface UpdateVfxScheduleRequest {
  name?: string;
  start_time?: string; // ISO 8601
  end_time?: string; // ISO 8601
  is_active?: boolean;
  vfx?: {
    enabled?: boolean;
    type?: VfxType;
    intensity?: VfxIntensity;
  };
  background?: {
    enabled?: boolean;
    url?: string | null;
    fallback?: string;
    theme?: number;
  };
  app_themes?: number | null;
}

// ===== TRANSACTION LIMITS & FRAUD DETECTION TYPES =====

export interface TransactionLimits {
  withdrawalAutoApproveThreshold: number;
  shopFrontingDebtThreshold?: number;
  maxDepositPerTransaction: number;
  maxDepositPerDay: number;
  maxWithdrawalPerDay: number;
  minWithdrawalAmount: number;
  minDepositAmount?: number;
  newAccountLimit: number;
  newAccountDays: number;
  maturityHours?: number;
  maturityGrandfatherDate?: string;
  /** UTC ISO datetime — 409 guards ignore transactions created before this when set. */
  gameWallet409GuardEnabledSince?: string | null;
  /** Cumulative auto-refund USD threshold before blocking external withdrawals. */
  autoRefundWarningAmountThreshold?: number;
  /** Pre-transaction game provider API health gate for deposit/withdraw/sync. */
  gameProviderHealthCheck?: GameProviderHealthCheckConfig;
  // KIOSK-317 Signup Bonus lives inside the transactionLimits JSONB block.
  signupBonus?: SignupBonusConfig;
}

export interface GameProviderHealthCheckConfig {
  enabled: boolean;
}

// KIOSK-317 — Signup Bonus real-time config (nested in transactionLimits).
export interface SignupBonusConfig {
  enabled: boolean;
  amountUsd: number;
  winThresholdUsd: number;
  maxCashoutUsd: number;
  minDepositUsd: number;
  expiryDays: number;
  dailyGrantBudget: number;
  providerRevSharePct: number;
}

export interface RateLimits {
  maxDepositsPerHour: number;
  maxWithdrawalsPerHour: number;
  maxGameTransfersPerHour: number;
  minTimeBetweenWithdrawalsMinutes: number;
}

export interface FraudFlags {
  highDepositVolumeThreshold: number;
  highDepositVolumeTimeWindowHours: number;
  rapidDepositWithdrawalTimeWindowMinutes: number;
  highDepositFrequencyCount: number;
  highDepositFrequencyTimeWindowMinutes: number;
  // Abnormal Profit Detection fields
  profitRatioThreshold: number;
  absoluteWinThreshold: number;
  newAccountProfitRatio: number;
  newAccountPeriodDays: number;
  lookbackPeriodDays: number;
  minDepositForAnalysis: number;
}

export interface TransactionLimitsConfig {
  transactionLimits: TransactionLimits;
  rateLimits: RateLimits;
  fraudFlags: FraudFlags;
  version: string;
  updatedAt: string;
  updatedBy: {
    id: string;
    email: string;
    name: string;
  } | null;
}

export interface TransactionLimitsConfigResponse {
  success: boolean;
  message: string;
  data: TransactionLimitsConfig;
  etag?: string;
}

export interface UpdateTransactionLimitsConfigRequest {
  transactionLimits?: Partial<TransactionLimits>;
  rateLimits?: Partial<RateLimits>;
  fraudFlags?: Partial<FraudFlags>;
}

export interface MessagingPlatformSettings {
  emailUnitPriceUsd: number;
  smsUnitPriceUsd: number;
  emailEnabled: boolean;
  smsEnabled: boolean;
  maxRecipientsPerCampaign: number;
  maxCampaignsPerShopPerDay: number;
  minCampaignIntervalMinutes: number;
  bannedWords: string[];
}

export interface MessagingPlatformSettingsResponse {
  success: boolean;
  data: MessagingPlatformSettings;
  message?: string;
}

export interface AdminShopMessagingPatchResponse {
  success: boolean;
  data: { shopId: string; messagingEnabled: boolean };
  message?: string;
}

export type SignupBonusStatus = 'inactive' | 'active' | 'in_review' | 'completed' | 'expired';

export interface SignupBonusGrant {
  id: string;
  userId: string;
  status: SignupBonusStatus;
  grantedAmount: string;
  realAmount: string;
  winThreshold: string;
  /** @deprecated Deposit is no longer a quest — may still appear in DB. */
  minDeposit?: string;
  peakNetWin: string;
  /** Live event P&L since claim (when API provides it). */
  netWin?: string | number | null;
  netWinMet: boolean;
  /** @deprecated Deposit is no longer a quest. */
  depositMet?: boolean;
  depositedTotal?: string;
  /** Live loss unlock flag when API provides it. */
  floorLiftedByLoss?: boolean;
  baselineBalance?: string;
  fundingSource: string | null;
  settlementReason: string | null;
  expiresAt: string;
  grantedAt: string | null;
  completedAt: string | null;
  expiredAt?: string | null;
  metadata?: Record<string, unknown> | null;
  user?: { id: string; username?: string; email?: string; phone?: string; shop_id?: string | null } | null;
}

export interface SignupBonusGrantsFilters {
  page?: number;
  limit?: number;
  status?: SignupBonusStatus | '';
  fundingSource?: string;
  shopId?: string;
}

export interface SignupBonusGrantsResponse {
  success: boolean;
  message: string;
  data: {
    grants: SignupBonusGrant[];
    pagination: { page: number; limit: number; total: number; pages: number };
  };
}

export interface SignupBonusKpiResponse {
  success: boolean;
  message: string;
  data: {
    totalGrants: number;
    byStatus: Record<SignupBonusStatus, number>;
    promoCostUsd: number;
  };
}

export type ProviderFeeOperation = 'deposit' | 'withdrawal' | 'transfer';

export interface ProviderFeeConfigItem {
  id: string;
  provider: string;
  operation: ProviderFeeOperation;
  /** Coin / network / method scope. 'all' means provider-wide default. */
  methodId?: string;
  /** Payment gateway portion of the total fee rate (0–1). */
  paymentGateFeeRate?: number;
  /** Minimum payment gateway fee in USD (withdrawal net model). */
  paymentGateFeeMinUsd?: number;
  /** Platform / system portion of the total fee rate (0–1). */
  systemFeeRate?: number;
  /** Sum of payment gate + system; same as historical single fee rate. */
  feeRate: number;
  /** Method visibility policy for player UI. */
  isEnabled?: boolean;
  networkFeeType?: 'none' | 'percent' | 'fixed';
  networkFeeRate?: number;
  networkFeeAmount?: number;
  /** 'zerox_live' when the network fee is streamed from the 0xprocessing Coins API (read-only). */
  networkFeeSource?: 'zerox_live' | 'config';
  /** Exact 0x currency behind a zerox_* method id, e.g. "USDT (TRC20)". */
  zeroxCoinCurrency?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface ProviderFeeConfigsResponse {
  success: boolean;
  message: string;
  data: {
    items: ProviderFeeConfigItem[];
    providers?: string[];
  };
}

export interface UpsertProviderFeeConfigsRequest {
  /** When true (default), only upsert rows in `items` without deleting others. */
  partial?: boolean;
  items: Array<{
    provider: string;
    operation: ProviderFeeOperation;
    /** optional; defaults to 'all' */
    methodId?: string;
    isEnabled?: boolean;
    paymentGateFeeRate: number;
    paymentGateFeeMinUsd?: number;
    systemFeeRate: number;
    networkFeeType?: 'none' | 'percent' | 'fixed';
    networkFeeRate?: number;
    networkFeeAmount?: number;
    /** @deprecated Prefer paymentGateFeeRate + systemFeeRate; if split omitted, legacy total maps to gate only. */
    feeRate?: number;
  }>;
}

export type FraudFlagType =
  | 'high_deposit_volume'
  | 'rapid_deposit_withdrawal'
  | 'high_deposit_frequency'
  | 'abnormal_profit_ratio'
  | 'abnormal_absolute_profit'
  | 'new_account_high_profit'
  | 'repeated_high_profit';
export type FraudFlagSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface FraudFlag {
  id: string;
  transactionId: string;
  userId: string;
  flagType: FraudFlagType;
  flagData: Record<string, any>;
  severity: FraudFlagSeverity;
  isResolved: boolean;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FraudFlagFilters {
  userId?: string;
  transactionId?: string;
  flagType?: FraudFlagType;
  severity?: FraudFlagSeverity;
  isResolved?: boolean;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'severity' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
}

export interface FraudFlagsResponse {
  success: boolean;
  message: string;
  data: {
    fraudFlags: FraudFlag[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
      sortBy?: string;
      sortOrder?: string;
      sortWarning?: string;
    };
  };
}

export interface ResolveFraudFlagRequest {
  notes?: string;
}

export interface ResolveFraudFlagResponse {
  success: boolean;
  message: string;
  data: FraudFlag;
}

// ===== MANUAL GAME DEPOSIT TYPES =====

export type ManualGameDepositStatus = 'pending' | 'completed' | 'expired' | 'rejected';

export interface UserGameAccount {
  id: string;
  gameUsername: string;
  gameAccountId: string | null;
  password: string | null;
  status: string;
  createdAt: string;
}

export interface ManualGameDeposit {
  transactionId: string;
  userId: string;
  userEmail: string;
  userPhone?: string;
  username: string;
  gameProvider: {
    id: string;
    name: string;
    code: string;
  };
  amount: number;
  currency: string;
  status: ManualGameDepositStatus;
  createdAt: string;
  expiresAt: string;
  timeRemaining: string | null;
  reservationId?: string;
  adminNotes: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  rejectionReason?: string | null;
  gameAccount?: UserGameAccount | null;
  syncMetadata?: {
    manualDeposit: boolean;
    reservationId: string;
    expiresAt: string;
  };
}

export interface ManualGameDepositFilters {
  status?: ManualGameDepositStatus;
  gameProviderId?: string;
  userId?: string;
  searchEmail?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'amount' | 'status';
  sortOrder?: 'ASC' | 'DESC';
}

export interface ManualGameDepositsResponse {
  success: boolean;
  data: {
    items: ManualGameDeposit[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    };
  };
}

export interface ManualGameDepositDetailResponse {
  success: boolean;
  data: ManualGameDeposit;
}

export interface ConfirmManualDepositRequest {
  gameUsername?: string;
  gamePassword?: string;
  adminNotes?: string;
}

export interface UpdateGameAccountRequest {
  gameUsername: string;
  gamePassword?: string;
}

export interface UpdateGameAccountResponse {
  success: boolean;
  data: {
    action: 'created' | 'updated';
    gameUsername: string;
    gameAccountId: string;
  };
}

export interface ConfirmManualDepositResponse {
  success: boolean;
  message: string;
  data: {
    transactionId: string;
    amount: number;
    status: string;
    confirmedAt: string;
    confirmedBy: string;
    gameProvider: {
      name: string;
      code: string;
    };
  };
}

export interface RejectManualDepositRequest {
  reason: string;
}

export interface RejectManualDepositResponse {
  success: boolean;
  message: string;
  data: {
    transactionId: string;
    amount: number;
    status: string;
    rejectedAt: string;
    rejectedBy: string;
    reason: string;
  };
}

// ===== ABNORMAL WITHDRAWAL PROTECTION TYPES =====

export type WithdrawalFlagReason =
  | 'profit_ratio'
  | 'absolute_win'
  | 'new_account'
  | 'rapid_pattern'
  | 'withdrawal_threshold'
  | 'related_flags';

export interface ProfitAnalysis {
  totalDeposits: number;
  totalWithdrawals: number;
  pendingWithdrawals: number;
  currentRequestAmount: number;
  totalWithdrawal?: number;
  profitRatio: number;
  netProfit: number;
  lookbackPeriodDays: number;
  isNewAccount: boolean;
  accountAgeDays: number;
  appliedThreshold: number;
  absoluteWinThreshold: number;
  ratioExceeded: boolean;
  absoluteExceeded: boolean;
  absoluteThresholdApplies?: boolean;
  skipped: boolean;
  applicable?: boolean;
  skipReason?: string;
  ownerType?: string | null;
  ledgerSource?: 'usd_wallet' | 'hierarchy_cash' | 'unknown';
  ledgerLabel?: string;
  withdrawalThresholdExceeded?: boolean;
  withdrawalThreshold?: number;
  withdrawalThresholdType?: string | null;
}

export interface WithdrawalAlertSummary {
  flagReason: WithdrawalFlagReason[];
  flagReasonMessages: string[];
  opsReviewMessages?: string[];
  fraudAlertMessages?: string[];
  riskScore: number;
  fraudRiskScore?: number;
  hasFraudSignals?: boolean;
  opsReviewOnly?: boolean;
  triggeredIndicators: Partial<Record<WithdrawalFlagReason, boolean>>;
  playerAge: number;
  totalDeposit: number;
  totalWithdrawal: number;
  profitRatio: number;
  relatedFlags: number;
  withdrawalThresholdExceeded: boolean;
  withdrawalThreshold: number;
  withdrawalThresholdType: string | null;
  isRepeatOffender: boolean;
  isNewAccount: boolean;
}

export interface ProfitAnalysisFlag {
  id: string;
  flagType: FraudFlagType;
  severity: FraudFlagSeverity;
  flagData: Record<string, any>;
  isResolved: boolean;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  createdAt: string;
}

export interface ProfitAnalysisResponse {
  success: boolean;
  data: {
    applicable?: boolean;
    subjectType?: 'player' | 'shop' | 'agent' | 'super_agent';
    transaction: {
      transactionId: string;
      amount: number;
      status: string;
      withdrawalType?: string | null;
      createdAt: string;
    };
    user: {
      id: string;
      username: string;
      email: string;
      createdAt: string;
      accountAgeDays: number;
      shop?: { id: string; name: string } | null;
      agent?: { id: string; name: string } | null;
      superAgent?: { id: string; name: string } | null;
    };
    profitAnalysis: ProfitAnalysis;
    alertSummary?: WithdrawalAlertSummary;
    flags: ProfitAnalysisFlag[];
    relatedFlags: number;
    /** @deprecated use relatedFlags */
    previousFlagCount?: number;
    shopName: string | null;
    agentName: string | null;
    superAgentName: string | null;
    flagReason: WithdrawalFlagReason[];
    flagReasonMessages: string[];
    opsReviewMessages?: string[];
    fraudAlertMessages?: string[];
    riskScore: number;
    fraudRiskScore?: number;
    hasFraudSignals?: boolean;
    opsReviewOnly?: boolean;
    playerAge: number;
    totalDeposit: number;
    totalWithdrawal: number;
    profitRatio: number;
  };
}

export interface AdjustBalanceRequest {
  amount: number;
  reason: string;
}

export interface AdjustBalanceResponse {
  success: boolean;
  message: string;
  data: {
    userId: string;
    amount: number;
    reason: string;
    newBalance?: number;
  };
}

export interface PlayerAutoRefundSummary {
  autoRefundCount: number;
  autoRefundAmount: number;
  autoRefundWarning: boolean;
  autoRefundWarningAt: string | null;
  autoRefundLastResetAt: string | null;
  autoRefundLastResetBy: string | null;
  warningThreshold: number;
}

export interface PlayerAutoRefundSummaryResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: PlayerAutoRefundSummary;
}

export interface ClearAutoRefundWarningRequest {
  adminNotes?: string;
}

export interface ClearAutoRefundWarningResponse {
  success: boolean;
  message: string;
  statusCode: number;
  timestamp: string;
  data: {
    userId: string;
    autoRefundCount: number;
    autoRefundAmount: number;
    autoRefundWarning: boolean;
  };
}

// ─── Domain Types ────────────────────────────────────────────────────────────

export type DomainType = 'kiosk' | 'mobile' | 'admin';

export interface Domain {
  id: string;
  domain: string;
  type: DomainType;
  ipWhitelist: string[] | null;
  isActive: boolean;
  label: string | null;
  shopId?: string | null;
  gameMappingCount?: number;
  created_at: string;
  updated_at: string;
}

export interface DomainsResponse {
  success: boolean;
  data: Domain[];
}

export interface DomainResponse {
  success: boolean;
  data: Domain;
}

export interface CreateDomainRequest {
  domain: string;
  type: DomainType;
  ipWhitelist?: string[] | null;
  isActive?: boolean;
  label?: string | null;
  cfCnameTarget?: string | null;
  game_ids?: string[];
}

export interface UpdateDomainRequest {
  type?: DomainType;
  ipWhitelist?: string[] | null;
  isActive?: boolean;
  label?: string | null;
  game_ids?: string[];
}

export interface DomainGameMapping {
  id: string;
  domainId: string;
  agentId?: string | null;
  gameId: string;
  isEnabled: boolean;
  gameProvider?: {
    id: string;
    code: string;
    name: string;
    status: string;
  };
}

export interface DomainGameMappingsResponse {
  success: boolean;
  data: {
    domain_id: string;
    game_ids: string[];
    mappings: DomainGameMapping[];
  };
}

// ─── Kiosk Health Types ───────────────────────────────────────────────────────

export interface KioskHealth {
  domainId: string;
  domain: string;
  label: string | null;
  ipAddress: string | null;
  lastSeenAt: string | null;
  isOnline: boolean;
  minutesSinceLastSeen: number | null;
}

export interface KioskHealthResponse {
  success: boolean;
  data: KioskHealth[];
}

// ─── Domain Analytics Types ───────────────────────────────────────────────────

export interface DomainAnalytics {
  domainId: string;
  domain: string;
  activeSessions24h: number;
  totalSessions: number;
  lastActivityAt: string | null;
}

export interface DomainAnalyticsResponse {
  success: boolean;
  data: DomainAnalytics;
}

// ─── CDN Agent Management (admin) ─────────────────────────────────────────────

export type CdnAgentStatus = 'active' | 'suspended' | 'terminated';

export interface PlayerWithdrawalAutoApproveChainItem {
  level: 'shop' | 'agent' | 'super_agent' | 'platform';
  override: number | null;
  name?: string | null;
  id?: string | null;
}

export interface PlayerWithdrawalAutoApproveSnapshot {
  override: number | null;
  effective: number;
  source: 'shop' | 'agent' | 'super_agent' | 'platform';
  platformDefault: number;
  chain: PlayerWithdrawalAutoApproveChainItem[];
}

export interface CdnAgent {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  superAgentId?: string | null;
  costRate?: string | number;
  status: CdnAgentStatus;
  suspendedAt?: string | null;
  suspendedBy?: string | null;
  terminatedAt?: string | null;
  terminatedBy?: string | null;
  /** Agent may send in-app popups to their shops */
  canCreateShopPopup?: boolean;
  /** Agent may manage popup permission for shops under this agent */
  canManageShopPopupPermission?: boolean;
  /** Default for new shops created under this agent (admin can override per shop) */
  defaultShopCanCreatePlayerPopup?: boolean;
  playerWithdrawalAutoApprove?: PlayerWithdrawalAutoApproveSnapshot;
  created_at?: string;
  updated_at?: string;
}

export interface AgentsListResponse {
  success: boolean;
  data: CdnAgent[];
}

export type AdminBroadcastAudience =
  | 'all_users'
  | 'user_ids'
  | 'all_agents'
  | 'agent_ids'
  | 'all_shops'
  | 'shop_ids';

export interface AdminBroadcastListItem {
  id: string;
  title: string;
  body: string;
  audience: AdminBroadcastAudience;
  targetIds: string[];
  /** Resolved labels: players → phone (fallback email), agents/shops → name */
  targetRecipients?: Array<{ id: string; display: string }>;
  expiresAt: string | null;
  createdAt: string | null;
  createdBy: string | null;
  sourceType?: 'admin' | 'agent' | 'shop';
  sourceId?: string | null;
  createdByAdmin: {
    id: string;
    username: string;
    email: string | null;
    name: string | null;
  } | null;
}

export interface AdminBroadcastsListResponse {
  success: boolean;
  data: {
    total: number;
    page: number;
    limit: number;
    items: AdminBroadcastListItem[];
  };
}

export interface AdminBroadcastShopsMetaResponse {
  success: boolean;
  data: {
    shops: Array<{
      id: string;
      name: string;
      email: string;
      agentId: string;
      agentName: string | null;
    }>;
  };
}

export type AdminNotificationSeverity = 'info' | 'warning' | 'critical';

export type AdminNotificationCategory =
  | 'finance'
  | 'operations'
  | 'support'
  | 'security'
  | 'announcement';

export interface AdminNotificationApiRow {
  id: string;
  title: string;
  body: string;
  severity: AdminNotificationSeverity;
  category: AdminNotificationCategory;
  actionUrl: string | null;
  entityType: string | null;
  entityId: string | null;
  aggregatedCount: number;
  isAnnouncement: boolean;
  createdAt: string;
  updatedAt?: string;
  acknowledged: boolean;
}

export interface CreateAdminBroadcastRequest {
  title: string;
  body: string;
  audience: AdminBroadcastAudience;
  targetIds?: string[];
  expiresAt?: string | null;
  /** In-app popups only: once (default) or sticky_daily (requires expiresAt; client shows again each reload until then) */
  displayMode?: 'once' | 'sticky_daily';
}

export interface CreateCdnAgentRequest {
  name: string;
  username: string;
  email?: string;
  phone?: string;
  password: string;
  super_agent_id: string;
  cost_rate?: number;
  can_create_shop_popup?: boolean;
  can_manage_shop_popup_permission?: boolean;
  default_shop_can_create_player_popup?: boolean;
}

export interface CreateCdnAgentResponse {
  success: boolean;
  data: CdnAgent;
  message?: string;
}

export interface PortalWithdrawalLimitsConfig {
  maxPerTransaction: number;
  maxPerDay: number;
  isUsingSystemDefault: boolean;
  source: 'system_default' | 'super_agent';
  systemDefaults: { maxPerTransaction: number; maxPerDay: number };
  superAgentOverrides?: {
    maxPerTransaction: number | null;
    maxPerDay: number | null;
  };
}

export interface SuperAgent {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  costRate?: string | number;
  /** Settlement config level (nullable in DB; UI may choose to force explicit selection). */
  configLevel?: 'shop' | 'agent' | 'super_agent' | null;
  /** API may return snake_case in some responses. */
  config_level?: 'shop' | 'agent' | 'super_agent' | null;
  /** When true, super agent may send popups/broadcasts down the tree and grant popup flags to managed agents. */
  canDelegatePopups?: boolean;
  maxPortalWithdrawalPerTransaction?: number | string | null;
  maxPortalWithdrawalPerDay?: number | string | null;
  max_portal_withdrawal_per_transaction?: number | string | null;
  max_portal_withdrawal_per_day?: number | string | null;
  portalWithdrawalLimits?: PortalWithdrawalLimitsConfig;
  playerWithdrawalAutoApprove?: PlayerWithdrawalAutoApproveSnapshot;
  status: 'active' | 'suspended' | 'terminated';
  created_at?: string;
  updated_at?: string;
}

export interface SuperAgentsListResponse {
  success: boolean;
  data: SuperAgent[];
}

export interface SuperAgentDetailResponse {
  success: boolean;
  data: {
    superAgent: SuperAgent;
    agents: CdnAgent[];
    shops: CdnShop[];
    /** CDN wallet for this super agent (null if never funded). */
    super_agent_wallet?: {
      balance: number;
      reserved_balance: number;
      currency: string;
      status: string;
    } | null;
  };
}

/** wallet_transactions rows involving the super agent’s wallet (deposits, transfers, player credits, etc.). */
export interface SuperAgentWalletTransactionItem {
  id: string;
  type: string;
  amount: number | null;
  balanceBefore: number | null;
  balanceAfter: number | null;
  description: string | null;
  status: string;
  direction: 'in' | 'out' | 'unknown';
  counterparty: { ownerType: string; ownerId: string } | null;
  fromWalletId: string | null;
  toWalletId: string | null;
  transactionId: string | null;
  referenceId: string | null;
  referenceType: string | null;
  costRate: number;
  costAmount: number;
  platformFeeRate: number;
  platformFeeAmount: number;
  createdAt?: string;
}

export interface SuperAgentWalletTransactionsResponse {
  success: boolean;
  data: {
    items: SuperAgentWalletTransactionItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface CreateSuperAgentRequest {
  name: string;
  username: string;
  email?: string;
  phone?: string;
  password: string;
  cost_rate?: number;
  can_delegate_popups?: boolean;
}

export interface CreateSuperAgentResponse {
  success: boolean;
  data: SuperAgent;
}

export type CdnShopStatus = 'active' | 'suspended' | 'terminated';

export interface CdnShop {
  id: string;
  agentId?: string;
  name: string;
  email: string;
  phone?: string | null;
  costRate?: string | number;
  status: CdnShopStatus;
  suspendedAt?: string | null;
  terminatedAt?: string | null;
  /** Shop may send in-app popups to players under this shop */
  canCreatePlayerPopup?: boolean;
  /** Paid email/SMS to players (shop messaging feature) */
  messagingEnabled?: boolean;
  playerWithdrawalAutoApprove?: PlayerWithdrawalAutoApproveSnapshot;
  created_at?: string;
  updated_at?: string;
}

export interface CdnAgentDomainGameMapping {
  id: string;
  domainId: string;
  gameId: string;
  isEnabled: boolean;
  gameProvider?: {
    id: string;
    code: string;
    name: string;
    status: string;
  };
}

export interface CdnAgentDomain {
  id: string;
  domain: string;
  type: DomainType;
  ipWhitelist?: string[] | null;
  isActive: boolean;
  label?: string | null;
  agentId?: string | null;
  game_ids?: string[];
  gameMappings?: CdnAgentDomainGameMapping[];
  created_at?: string;
  updated_at?: string;
}

export interface CdnWalletSummary {
  balance: number;
  reserved_balance: number;
  currency?: string;
  status?: string;
}

export interface AdminAgentDetailResponse {
  success: boolean;
  data: {
    agent: CdnAgent;
    shops: CdnShop[];
    agent_wallet: CdnWalletSummary | null;
  };
}

export interface CreateCdnShopRequest {
  name: string;
  username?: string;
  email: string;
  phone?: string;
  password: string;
  cost_rate?: number;
}

export interface CreateCdnShopResponse {
  success: boolean;
  data: CdnShop;
}

export interface AdminShopDetailPlayer {
  id: string;
  username?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string;
  shop_id?: string | null;
  shop_assigned_at?: string | null;
  created_at?: string;
  wallet: CdnWalletSummary | null;
}

export interface AdminShopDetailResponse {
  success: boolean;
  data: {
    /** Present when loaded via GET /admin/shops/:shopId */
    agent_id?: string;
    shop: CdnShop;
    shop_wallet: CdnWalletSummary | null;
    players: AdminShopDetailPlayer[];
  };
}

export interface AdminShopCreditAdjustRequest {
  direction: 'credit' | 'debit';
  amount: number;
  /** When set, legacy credit-adjust uses a stable ledger transaction id (admin + shop flows). */
  idempotencyKey?: string;
}

export interface AdminShopCreditAdjustResponse {
  success: boolean;
  data: {
    balance: number;
    reserved_balance: number;
    idempotent?: boolean;
    ledger_wallet_transaction_id?: string | null;
  };
}

export type AdminFinanceCorrectionOwnerType = 'shop' | 'agent' | 'super_agent' | 'player';

export interface AdminFinanceCorrectionRequest {
  ownerType: AdminFinanceCorrectionOwnerType;
  ownerId: string;
  walletKind: 'usd' | 'cdn_credit';
  direction: 'debit';
  amount: number | string;
  reason: string;
  ticketId: string;
  idempotencyKey: string;
}

export interface AdminFinanceCorrectionBalancesAfter {
  balance?: number;
  reserved_balance?: number;
  balance_gross?: number;
  balance_net?: number;
  reserved_gross?: number;
  reserved_net?: number;
}

export interface AdminFinanceCorrectionResponseData {
  success: boolean;
  idempotent: boolean;
  ownerType: string;
  ownerId: string;
  walletKind: string;
  balances_after: AdminFinanceCorrectionBalancesAfter;
}

export interface AdminFinanceCorrectionResponse {
  success: boolean;
  data: AdminFinanceCorrectionResponseData;
}

// ===== Cashier Management (Admin) =====
export type CashierStatus = 'active' | 'suspended' | 'terminated';

export interface AdminCashierListItem {
  id: string;
  shopId: string;
  username: string;
  status: CashierStatus;
  lastLoginAt: string | null;
  terminatedAt: string | null;
  agent: { id: string; name: string; email?: string | null; phone?: string | null; status?: string } | null;
  shop: { id: string; agentId: string; name: string; email?: string | null; phone?: string | null; status?: string } | null;
  currentShift: { id: string; openedAt: string; openingCashBalance: string | number } | null;
  totalTransactionsToday: number;
}

export interface AdminCashiersListResponse {
  success: boolean;
  data: AdminCashierListItem[];
  meta: {
    page: number;
    limit: number;
    total: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    sortWarning?: string;
  };
}

export interface AdminCreateCashierRequest {
  shopId: string;
  username: string;
  password: string;
}

export interface AdminCreateCashierResponse {
  success: boolean;
  data: { id: string; shopId: string; username: string; status: CashierStatus };
}

export interface AdminCashierReportResponse {
  success: boolean;
  data: {
    cashierId: string;
    shopId: string;
    period: 'daily' | 'weekly' | 'monthly' | string;
    from: string;
    to: string;
    totals: {
      deposits: { total: number; count: number };
      redemptions: { total: number; count: number };
      withdrawals: { total: number; count: number };
    };
  };
}

export interface AdminCashierShiftHistoryResponse {
  success: boolean;
  data: Array<{
    id: string;
    openedAt: string;
    closedAt: string | null;
    openingCashBalance: string | number;
    expectedClosingCash: string | number | null;
    closingCashCounted: string | number | null;
    discrepancyAmount: string | number | null;
    discrepancyFlagged: boolean;
    closedReason: string;
  }>;
}

export interface AdminFraudIndicatorsResponse {
  success: boolean;
  data: {
    from: string;
    to: string;
    thresholds: { highRedemptionThreshold: number; discrepancyThreshold: number };
    highRedemptions: Array<{ cashierId: string; totalRedeem: number }>;
    discrepancies: Array<{ shiftId: string; cashierId: string; discrepancyAmount: number; openedAt: string; closedAt: string }>;
    outsideShiftHours: Array<{
      cashierTransactionId: string;
      cashierId: string;
      shiftId: string;
      occurredAt: string;
      shiftOpenedAt: string | null;
      shiftClosedAt: string | null;
      type: string;
      amount: number;
    }>;
  };
}

export interface AdminAgentCashFundingRequest {
  /** Legacy alias for credits */
  amount?: number;
  credits?: number;
  cash_received?: number;
  cashReceived?: number;
  payment_method?: string;
  paymentMethod?: string;
  received_at?: string;
  receivedAt?: string;
  external_reference?: string;
  externalReference?: string;
  note?: string;
}

export interface AdminAgentCashFundingResponse {
  success: boolean;
  data: {
    payment_transaction: Record<string, unknown>;
    credit_transaction: Record<string, unknown>;
    cash_transaction?: Record<string, unknown>;
    new_balance: number;
  };
}

export interface CdnPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** Payment rows where buyer is this agent (purchases, admin cash, etc.) */
export interface AdminAgentPaymentTransactionRow {
  id: number;
  transactionId: string;
  type?: string;
  provider?: string;
  paymentMethod?: string | null;
  amount?: string | number;
  currency?: string;
  status?: string;
  description?: string | null;
  createdAt?: string;
  processedAt?: string | null;
  buyerType?: string | null;
  buyerId?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface AdminAgentPaymentTransactionsResponse {
  success: boolean;
  data: {
    transactions: AdminAgentPaymentTransactionRow[];
    pagination: CdnPagination;
  };
}

/** Agent CDN wallet ledger row (wallet_transactions). */
export interface AdminAgentWalletTransactionRow {
  id: string;
  type: string;
  amount: number | null;
  balanceBefore: number | null;
  balanceAfter: number | null;
  description: string | null;
  status: string;
  direction: 'in' | 'out' | 'unknown';
  counterparty: { ownerType: string; ownerId: string } | null;
  fromWalletId: string | null;
  toWalletId: string | null;
  transactionId: string | null;
  referenceId: string | null;
  referenceType: string | null;
  createdAt?: string;
}

export interface AdminAgentWalletTransactionsResponse {
  success: boolean;
  data: {
    transactions: AdminAgentWalletTransactionRow[];
    pagination: CdnPagination;
  };
}

export type CostEventType = 'shop_direct_topup' | 'user_direct_topup';

export interface CostReportRow {
  event_type: CostEventType;
  transaction_at: string;
  transaction_id: string;
  payment_transaction_id: number;
  currency: string;
  user_id: string | null;
  username: string | null;
  shop_id: string;
  shop_name: string;
  agent_id: string;
  agent_name: string;
  gross_amount: number;
  credit_amount: number;
  shop_cash_in: number;
  shop_cost_rate: number;
  agent_cost_rate: number;
  shop_cost_amount: number;
  agent_cost_amount: number;
  agent_margin_amount: number;
}

export interface CostReportResponse {
  success: boolean;
  data: {
    rows: CostReportRow[];
    summary: {
      total_rows: number;
      total_gross_amount: number;
      total_credit_amount: number;
      total_shop_cost: number;
      total_agent_cost: number;
      total_agent_margin: number;
    };
    pagination: CdnPagination;
  };
}

// ===== Integration API Logs =====

export type IntegrationLogTab = 'game' | 'payment' | 'webhooks';

export interface IntegrationLogListItem {
  id: string | number;
  providerCode?: string;
  apiMethod?: string;
  requestMethod?: string;
  success?: boolean;
  responseStatus?: number | null;
  durationMs?: number | null;
  userId?: string | null;
  gameProviderId?: string | null;
  paymentTransactionId?: number | null;
  referenceId?: string | null;
  transactionId?: string;
  orderNumber?: string | null;
  status?: string | null;
  amount?: string | number | null;
  currency?: string | null;
  processed?: boolean;
  processedAt?: string | null;
  errorMessage?: string | null;
  createdAt: string;
}

export interface IntegrationOutboundLogDetail extends IntegrationLogListItem {
  requestUrl?: string;
  requestHeaders?: Record<string, unknown> | null;
  requestBody?: Record<string, unknown> | null;
  responseHeaders?: Record<string, unknown> | null;
  responseBody?: Record<string, unknown> | null;
  errorStack?: string | null;
  metadata?: Record<string, unknown> | null;
  updatedAt?: string;
}

export interface IntegrationWebhookLogDetail extends IntegrationLogListItem {
  uid?: string | null;
  args?: string | null;
  signature?: string | null;
  rawData?: Record<string, unknown> | null;
  rawHeaders?: Record<string, unknown> | null;
  rawBody?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  updatedAt?: string;
}

export interface IntegrationLogFilters {
  from: string;
  to: string;
  page?: number;
  limit?: number;
  providerCode?: string;
  apiMethod?: string;
  success?: string;
  userId?: string;
  gameProviderId?: string;
  paymentTransactionId?: string;
  transactionId?: string;
  referenceId?: string;
  requestUrl?: string;
  minDurationMs?: string;
  processed?: string;
  status?: string;
  orderNumber?: string;
}

export interface IntegrationLogListResponse {
  success: boolean;
  message?: string;
  data: {
    logs: IntegrationLogListItem[];
    pagination: Pagination & { totalPages?: number };
    filters: Record<string, unknown>;
  };
}

export interface IntegrationOutboundLogDetailResponse {
  success: boolean;
  message?: string;
  data: {
    log: IntegrationOutboundLogDetail;
  };
}

export interface IntegrationWebhookLogDetailResponse {
  success: boolean;
  message?: string;
  data: {
    log: IntegrationWebhookLogDetail;
  };
}
